import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardBody, CardHeader, Label, Badge, Input, Button } from "reactstrap";
import Loader from "../../Components/Common/Loader";
import ForeCastPreviewCardHeader from "../../Components/Common/ForeCastPreviewCardHeader";
import Chart from "react-apexcharts";
import { DISCOUNT } from "../../helpers/data_helper";
import { postDataAPI } from "../../helpers/phpbackend_helper";

const DiscountTrends = ({ mealPeriod, leftFilter, discountCodes, serveList, allDiscountList, changeDiscountCodes, onApiStart, discountListReady = false }) => {
    const [doccupancy, setDoccupancy] = useState('Table');
    const [selectedDiscounts, setSelectedDiscounts] = useState([]);
    const [updateDiscounts, setUpdateDiscounts] = useState([]);
    const changeOccupancy = () => {
        setDoccupancy(doccupancy === "Seat" ? "Table" : "Seat");
    }
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hours, setHours] = useState([]);
    const controllerRef = useRef(null);
    const apiStartSentRef = useRef(false);

    const notifyApiStart = () => {
        if (apiStartSentRef.current) {
            return;
        }
        apiStartSentRef.current = true;
        if (typeof onApiStart === "function") {
            onApiStart();
        }
    };

    // Reset local gate when filters / discount catalog change
    useEffect(() => {
        apiStartSentRef.current = false;
        setSelectedDiscounts([]);
        setUpdateDiscounts([]);
        setSeries([]);
        setHours([]);
        setLoading(!discountListReady);
    }, [mealPeriod, leftFilter, serveList, doccupancy, discountListReady]);

    // Select all discounts only after parent finished loading the discount list.
    // Do NOT treat the initial empty [] as "ready" — that caused Performance to start
    // before the real heatmap API.
    useEffect(() => {
        if (!discountListReady || !Array.isArray(allDiscountList)) {
            return;
        }
        if (allDiscountList.length === 0) {
            // Truly no discounts — stop loader, skip heatmap API, unblock Performance
            setLoading(false);
            setSeries([]);
            setHours([]);
            setSelectedDiscounts([]);
            setUpdateDiscounts([]);
            notifyApiStart();
            return;
        }
        const allValues = allDiscountList.map((d) => d.value).filter(Boolean);
        setSelectedDiscounts(allValues);
        setUpdateDiscounts(allValues);
    }, [mealPeriod, leftFilter, serveList, doccupancy, allDiscountList, discountListReady]);


    // Heatmap row order expected by the chart (high occupancy → low)
    const OCCUPANCY_BUCKET_ORDER = [
        "91-100%",
        "81-90%",
        "71-80%",
        "61-70%",
        "51-60%",
        "41-50%",
        "31-40%",
        "21-30%",
        "11-20%",
        "0-10%",
    ];

    const parseHourToMinutes = (t) => {
        const match = String(t).match(/^(\d{1,2})(?::(\d{2}))?(AM|PM)$/i);
        if (!match) return 0;
        let hour = parseInt(match[1], 10);
        const minutes = parseInt(match[2] || "0", 10);
        const meridiem = match[3].toUpperCase();
        if (meridiem === "PM" && hour !== 12) hour += 12;
        if (meridiem === "AM" && hour === 12) hour = 0;
        return hour * 60 + minutes;
    };

    const fetchData = async (discountIds) => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        const controller = new AbortController();
        controllerRef.current = controller;
        setLoading(true);
        setSeries([]);

        // Signal only when the real heatmap request is about to go out
        notifyApiStart();

        try {
            const response = await postDataAPI({
                serviceName: DISCOUNT.serviceName,
                op: DISCOUNT.op_discount_occupancy_heatmap,
                leftFilter,
                mealPeriod,
                server_id: serveList,
                discount_id: discountIds,
                occupancy: doccupancy === "Table" ? "table_occ" : "seat_occ",
                fromPercentage: -1,
            }, controller.signal);

            if (controller.signal.aborted) {
                return;
            }

            if (response.status !== "ok") {
                console.error("Heatmap API error:", response.message);
                setSeries([]);
                setHours([]);
                return;
            }

            // Backend returns { data: [{ name, data: [{x,y}] }], hours: [] }
            const rawData = Array.isArray(response.data?.data) ? response.data.data : [];
            const responseHours = Array.isArray(response.data?.hours) ? response.data.hours : [];

            const uniqueHours = [...responseHours].sort(
                (a, b) => parseHourToMinutes(a) - parseHourToMinutes(b)
            );

            setHours(uniqueHours);

            const seriesByName = {};
            rawData.forEach((item) => {
                const hourMap = {};
                (Array.isArray(item.data) ? item.data : []).forEach((point) => {
                    if (point?.x == null || point?.y === undefined) {
                        return;
                    }
                    let total = 0;
                    let breakdown = {};
                    if (point.y && typeof point.y === "object") {
                        breakdown = point.y;
                        total = Object.values(point.y).reduce((sum, val) => sum + Number(val || 0), 0);
                    } else {
                        total = Number(point.y) || 0;
                    }
                    hourMap[point.x] = { y: total, meta: breakdown };
                });

                seriesByName[item.name] = {
                    name: item.name,
                    data: uniqueHours.map((hour) => ({
                        x: hour,
                        y: hourMap[hour]?.y ?? 0,
                        meta: hourMap[hour]?.meta ?? {},
                    })),
                };
            });

            // Keep all buckets in chart order even if SP returned none for a bucket
            const finalSeries = OCCUPANCY_BUCKET_ORDER.map((bucketName) => {
                if (seriesByName[bucketName]) {
                    return seriesByName[bucketName];
                }
                return {
                    name: bucketName,
                    data: uniqueHours.map((hour) => ({ x: hour, y: 0, meta: {} })),
                };
            });

            setSeries(finalSeries);
        } catch (err) {
            if (err.name === "CanceledError" || err.code === "ERR_CANCELED" || err.name === "AbortError") {
                console.log("Request cancelled");
                return;
            }
            console.error("API Error:", err);
            setSeries([]);
            setHours([]);
        } finally {
            // Always clear loader for this request; aborted requests previously left it spinning
            if (controllerRef.current === controller) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!leftFilter) {
            return undefined;
        }
        // Do not call API with empty discount_id — that uses blank-discount SP path (wrong results)
        if (!Array.isArray(updateDiscounts) || updateDiscounts.length === 0) {
            return undefined;
        }

        const timeout = setTimeout(() => {
            fetchData(updateDiscounts);
        }, 300);

        return () => {
            clearTimeout(timeout);

            if (controllerRef.current) {
                controllerRef.current.abort();
            }
        };
    }, [updateDiscounts, leftFilter, mealPeriod, serveList, doccupancy]);

    const handleDiscountClick = (badge) => {
        const updatedBadges = selectedDiscounts.includes(badge)
            ? selectedDiscounts.filter((value) => value !== badge)
            : [...selectedDiscounts, badge];

        setSelectedDiscounts(updatedBadges);
    };

    const handleReload = () => {
        if (!selectedDiscounts.length) {
            // Keep UI honest: empty selection must not hit blank-discount SP path
            setSeries([]);
            setHours([]);
            return;
        }
        setUpdateDiscounts([...selectedDiscounts]);
    };

    const generateDynamicRanges = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            return [
                { from: 0, to: 0, name: "Extremely Low", color: "#f8f9fa" },
                { from: 1, to: 1, name: "Very Low", color: "#d3f2c2" },
            ];
        }

        const allValues = data.flatMap((row) =>
            (row.data || []).map((point) => Number(point.y) || 0)
        );
        const maxValue = Math.max(0, ...allValues);
        const step = Math.max(1, Math.ceil(maxValue / 5));

        return [
            { from: 0, to: 0, name: "Extremely Low", color: "#f8f9fa" },
            { from: 1, to: step, name: "Very Low", color: "#d3f2c2" },
            { from: step + 1, to: 2 * step, name: "Low", color: "#91e05c" },
            { from: 2 * step + 1, to: 3 * step, name: "Medium", color: "#f9d776" },
            { from: 3 * step + 1, to: 4 * step, name: "High", color: "#f8ac5c" },
            { from: 4 * step + 1, to: Math.max(maxValue, 4 * step + 1), name: "Very High", color: "#f17171" },
        ];
    };



    const dynamicColorRanges = generateDynamicRanges(series);

    const xAxisOffsetX = useMemo(() => {
        if (window.innerWidth > 2000) return -850;
        if (window.innerWidth > 1800) return -740;
        if (window.innerWidth > 1700) return -680;
        if (window.innerWidth > 1500) return -590;
        if (window.innerWidth > 1500) return -590;
        if (window.innerWidth > 1350) return -520;
        if (window.innerWidth > 1200) return -450;
        if (window.innerWidth > 1000) return -370;
        if (window.innerWidth > 768) return -320;
        return -100;
    }, []);

    const handleSelectAllToggle = () => {
        if (selectedDiscounts.length === allDiscountList.length) {
            setSelectedDiscounts([]);
        } else {
            const allValues = allDiscountList.map(d => d.value);
            setSelectedDiscounts(allValues);
        }
    };

    const noDiscounts =
        discountListReady && Array.isArray(allDiscountList) && allDiscountList.length === 0;
    const showLoader = !noDiscounts && (loading || !discountListReady || series.length === 0);

    const heatmapHeader = (
        <CardHeader className="align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">
                Discount Heatmap by Hour and Occupancy
            </h4>
            <div className="flex-shrink-0">
                <div className="form-check form-switch form-switch-right form-switch-md d-flex align-items-center gap-2">
                    <Label className="form-label text-muted mb-0" style={{ marginRight: "5px" }}>Seat</Label>
                    <Input
                        className="form-check-input"
                        type="checkbox"
                        checked={doccupancy === "Table"}
                        onChange={changeOccupancy}
                        disabled={noDiscounts}
                    />
                    <Label className="form-label text-muted mb-0 ms-2">Table</Label>
                    <Button
                        color="primary"
                        className="btn-label rounded-pill btn-sm"
                        onClick={handleReload}
                        disabled={noDiscounts}
                    >
                        <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i> Reload
                    </Button>
                </div>
            </div>
        </CardHeader>
    );

    return (
        <Card className="position-relative" style={{ minHeight: showLoader ? 280 : undefined }}>
            {heatmapHeader}
            {noDiscounts ? (
                <CardBody className="py-5 text-center text-muted">
                    No discounts available for the selected filters.
                </CardBody>
            ) : series.length > 0 ? (
                <CardBody>
                        {(loading) && <Loader />}
                        <div id="DiscountTrends"
                            className={
                                loading
                                    ? "opacity-0 table-responsive table-card"
                                    : "table-responsive table-card"
                            }
                            style={{ maxHeight: '780px', overflow: 'hidden', overflowX: 'scroll' }}
                        >

                            <div className="d-flex justify-content-between align-items-center ms-3 mt-1 mr-5">
                                <Label className="ms-1 mt-1 fw-normal">Number of (promotional) discounts given across time and occupancy levels</Label>
                                <Badge
                                    color="primary"
                                    onClick={handleSelectAllToggle}
                                    style={{
                                        minWidth: "120px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        backgroundColor: "#364574",
                                        color: "#fff",
                                        border: "1px solid #4A5882",
                                        marginRight: "30px"
                                    }}
                                >
                                    {selectedDiscounts.length === allDiscountList.length ? "Deselect All Discount" : "Select All Discount"}
                                </Badge>
                            </div>
                            <div className="d-flex flex-wrap gap-2 ms-3">
                                {allDiscountList.map((badge) => (
                                    <Badge
                                        key={badge.value}
                                        color={selectedDiscounts.includes(badge.value) ? "danger" : "light"}
                                        onClick={() => handleDiscountClick(badge.value)}
                                        style={{
                                            cursor: "pointer",
                                            backgroundColor: selectedDiscounts.includes(badge.value) ? "" : "#d3d3d3", // light gray
                                            color: selectedDiscounts.includes(badge.value) ? "" : "#000", // black text
                                            border: selectedDiscounts.includes(badge.value) ? "1px solid #F06548" : "1px solid #ccc" // optional, for subtle outline
                                        }}
                                    >
                                        {badge.label}
                                    </Badge>
                                ))}

                            </div>
                            <Chart
                                options={{
                                    chart: { type: "heatmap" },
                                    dataLabels: {
                                        enabled: true,
                                        style: { colors: ['#000'] }
                                    },
                                    xaxis: {
                                        categories: hours, position: "top",
                                        labels: { show: true, style: { fontSize: '12px' } },
                                        title: {
                                            text: "Occupancy",
                                            offsetX: xAxisOffsetX,
                                            offsetY: 18,
                                            style: {
                                                fontSize: "10px"
                                            }
                                        }
                                    },
                                    yaxis: {
                                        labels: { show: true, style: { fontSize: '12px' } },
                                    },
                                    plotOptions: {
                                        heatmap: {
                                            radius: 0,
                                            enableShades: false,
                                            colorScale: {
                                                ranges: dynamicColorRanges,
                                            },


                                        }
                                    },
                                    grid: { padding: { right: 0, left: 30 } },
                                    legend: { show: false },
                                    colors: [],
                                    tooltip: {
                                        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                                            const data = w.config.series[seriesIndex].data[dataPointIndex];
                                            const breakdown = data.meta || {};

                                            const title = `<div style="font-weight: bold; margin-bottom: 6px;">
                                            ${w.config.series[seriesIndex].name}
                                        </div>`;

                                            const rows = Object.entries(breakdown).map(([key, value]) =>
                                                `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                                    <span style="color: #444;">${key}: </span> 
                                    <span style="color: #000;"> &nbsp; ${value}</span>
                                </div>`
                                            ).join('');

                                            const container = `
                                <div style="padding: 10px; max-width: 250px; font-size: 13px; font-family: sans-serif;">
                                    ${title}
                                    ${rows || '<div style="color: #999;">No data</div>'}
                                </div>
                            `;

                                            return container;
                                        }
                                    }


                                }}
                                series={series}
                                type="heatmap"
                                height={500}
                            />

                        </div>
                    </CardBody>
            ) : (
                <CardBody className="position-relative" style={{ minHeight: 200 }}>
                    <Loader />
                </CardBody>
            )}
        </Card>
    );
};

export default DiscountTrends;
